/**
 * Versioned checklist catalogues.
 *
 * A report is stamped with the catalogue version it was started on
 * (`Report.checklistVersion`) and is validated and rendered against THAT
 * version forever — a v2 shipping mid-inspection does not move an open v1
 * draft, and a v1 report opened in 2030 still finds its steps. Hence:
 *
 * - **A published version is frozen.** Never edit v1's steps, chips, capture
 *   minimums or `appliesTo` once a report has used it — publish v2 instead.
 *   Labels (`labels.ru.ts`) are not part of the version and may be reworded.
 * - **Versions are never deleted.** Every client ships every version.
 * - Step / chip keys come from the append-only registries in `keys.ts`.
 *
 * v1 is the route of the picker-flow mockups (`design/mockups/picker-flow.html`):
 * 30 steps in 8 sections plus the A5 «Диагностика и файлы» step before the
 * finish screen — 31 in total.
 *
 * v2 (AUT-465) is the same route with the five body steps in point mode
 * (`catalogue.v2.body.ts`). It is CURRENT (what a new client report starts
 * on) since AUT-469, which flipped `CURRENT_CHECKLIST_VERSION` last, after the
 * api learned v2 and together with the mobile point flow. v1 stays frozen
 * and creatable.
 */
import { V1_INSIDE } from './catalogue.v1.inside.js';
import { V1_OUTSIDE } from './catalogue.v1.outside.js';
import { toCatalogueV2 } from './catalogue.v2.body.js';
/**
 * Categories a step may file media under. Mirrors api
 * `REPORT_MEDIA_CATEGORIES` minus the ones no step uses, PLUS `diagnostics`,
 * which the api does not accept yet. Adding it to the api list and pinning the
 * two lists against each other with an api guard spec is AUT-387 (P-20).
 */
export const CHECKLIST_CAPTURE_CATEGORIES = [
    'exterior',
    'interior',
    'engine',
    'wheels',
    'undercarriage',
    'document',
    'walkaround',
    'diagnostics',
];
const stamp = (drafts) => drafts.map((draft, index) => ({ ...draft, order: index + 1 }));
export const CHECKLIST_CATALOGUES = {
    1: stamp([...V1_OUTSIDE, ...V1_INSIDE]),
    2: stamp(toCatalogueV2([...V1_OUTSIDE, ...V1_INSIDE])),
};
/** The version new reports start on. */
export const CURRENT_CHECKLIST_VERSION = 2;
/**
 * Versions a report may be CREATED on (the api's create check). A superset of
 * `CURRENT_CHECKLIST_VERSION`: a version becomes creatable before a client
 * starts on it, so the server is ready when the client flips.
 *
 * v1 must stay creatable until old clients are cut off by the min-version
 * gate: a v1 draft started offline before the upgrade still POSTs create with
 * version 1, and a 409 is permanent on the mobile side (its sync goes
 * 'closed'), so dropping v1 here would strand that draft for good.
 */
export const CREATABLE_CHECKLIST_VERSIONS = [1, 2];
/** `undefined` for a version this build does not know (tolerant read). */
export const getChecklistCatalogue = (version) => CHECKLIST_CATALOGUES[version];
/** A step of a given version, or `undefined` (unknown version or key). */
export const getStepDef = (version, stepKey) => CHECKLIST_CATALOGUES[version]?.find((step) => step.key === stepKey);
//# sourceMappingURL=catalogue.js.map